import {
  EMAIL_TEMPLATE_LEADERSHIP,
  EMAIL_TEMPLATE_PE_BOARD,
  inviteSubjectForPack,
  PACK_GENERIC,
} from "./inviteEmailTemplates";
import {
  CLASSIC_INVITE_ROLES,
  INVITE_STATUSES,
  rolesFromWorkspace,
  workspaceInviteDefaults,
  workspaceLiteInviteAvailable,
} from "./inviteConstants";
import { assessmentModeLabel } from "./inviteOptions";
import { LENS_ROUTES } from "./routes";

const COMPLETED = new Set(["Completed"]);
const PENDING = new Set(["Pending", "Not started", "LiteCompleted"]);
const STEP_LABELS = ["Company", "Participants", "Done"];

const TEMPLATE_LABELS = {
  [EMAIL_TEMPLATE_LEADERSHIP]: "Leadership Team (CEO / Leadership)",
  [EMAIL_TEMPLATE_PE_BOARD]: "PE Sponsor & Board Member",
};

function serializeInviteRow(row) {
  if (!row || typeof row !== "object") return null;
  const first = row.first_name || "";
  const last = row.last_name || "";
  return {
    invite_id: row.invite_id || row.id || null,
    company_name: row.company_name || null,
    invited_by: row.invited_by_name || row.invited_by?.name || null,
    email: row.email || null,
    first_name: first || null,
    last_name: last || null,
    full_name: [first, last].filter(Boolean).join(" ") || row.full_name || null,
    designation: row.designation || null,
    status: row.status || null,
    created_date: row.created_date || row.created_at || null,
    user_id: row.user_id || null,
    has_survey_token: !!(row.survey_token || row.token),
  };
}

export function buildInvitesListContext(invites, filterText, sortKey, sortDir, totalLoaded) {
  const companyMap = new Map();
  for (const row of invites) {
    const name = (row.company_name || "Unknown").trim() || "Unknown";
    if (!companyMap.has(name)) {
      companyMap.set(name, {
        company: name,
        invited: 0,
        completed: 0,
        pending: 0,
        notStarted: 0,
        liteCompleted: 0,
      });
    }
    const c = companyMap.get(name);
    c.invited += 1;
    const st = row.status || "";
    if (COMPLETED.has(st)) c.completed += 1;
    else if (PENDING.has(st)) {
      if (st === "Not started") c.notStarted += 1;
      else if (st === "LiteCompleted") c.liteCompleted += 1;
      else c.pending += 1;
    }
  }

  const companyRollup = [...companyMap.values()]
    .map((c) => ({
      ...c,
      completionPct: c.invited ? Math.round((c.completed / c.invited) * 100) : 0,
      stuck: c.pending + c.notStarted + c.liteCompleted,
    }))
    .sort((a, b) => a.completionPct - b.completionPct);

  const totalInvited = invites.length;
  const totalCompleted = invites.filter((r) => COMPLETED.has(r.status)).length;
  const totalPending = invites.filter(
    (r) => PENDING.has(r.status) && r.status !== "Not started" && r.status !== "LiteCompleted"
  ).length;
  const totalNotStarted = invites.filter((r) => r.status === "Not started").length;
  const totalLite = invites.filter((r) => r.status === "LiteCompleted").length;
  const completionPct = totalInvited ? Math.round((totalCompleted / totalInvited) * 100) : 0;

  return {
    page: "invites",
    surface: "engagement",
    currentRoute: LENS_ROUTES.engagementPipeline,
    pagePath: LENS_ROUTES.engagementPipeline,
    activeLens: "pipeline",
    scope: {
      label: "Invited participants",
      text: filterText.trim()
        ? `${invites.length} invites match search (${totalLoaded} loaded total)`
        : `${invites.length} participant invites (${totalLoaded} loaded)`,
    },
    filters: {
      search: filterText.trim() || null,
      sortKey,
      sortDir,
    },
    kpis: {
      totalInvites: totalInvited,
      totalCompleted,
      totalPending,
      totalNotStarted,
      totalLiteCompleted: totalLite,
      completionPct,
      companyCount: companyRollup.length,
    },
    companyRollup,
    companies: companyRollup.map((c) => ({
      name: c.company,
      invited: c.invited,
      completed: c.completed,
      completionPct: c.completionPct,
      stuck: c.stuck,
      pending: c.pending,
      notStarted: c.notStarted,
      liteCompleted: c.liteCompleted,
    })),
    organizationRows: invites.slice(0, 200).map((r) => ({
      name: r.company_name,
      email: r.email,
      status: r.status,
      designation: r.designation,
      invited: 1,
      completed: r.status === "Completed" ? 1 : 0,
    })),
    participants: invites.slice(0, 200).map(serializeInviteRow).filter(Boolean),
  };
}

const PILLAR_KEYS = [
  "strategic",
  "scalable",
  "relentless",
  "disciplined",
  "energized",
  "financial",
];

/** 5x assessment overall scores from Insights API (not invite completion %). */
export function buildAssessmentScoresContext(
  organizations,
  { isFiveX = true, productName = "Team Assessment" } = {}
) {
  const orgs = organizations || [];
  const scored = orgs
    .filter((o) => o && o.reportReady && o.overall != null)
    .map((o) => {
      const row = {
        name: o.name,
        overall: o.overall,
        reportReady: true,
        invited: o.invited,
        completed: o.completed,
      };
      for (const pk of PILLAR_KEYS) {
        if (o[pk] != null) row[pk] = o[pk];
      }
      return row;
    })
    .sort((a, b) => (b.overall ?? 0) - (a.overall ?? 0));

  const notReady = orgs
    .filter((o) => o && o.name && !o.reportReady)
    .map((o) => o.name)
    .slice(0, 20);

  return {
    metric: isFiveX ? "5x_assessment_overall" : "assessment_overall",
    description: isFiveX
      ? "Overall resilience score from completed 5x Team Assessments (Insights). " +
        "NOT the same as invite completion percentage (see portfolio.companies.completionPct)."
      : `Overall score from completed ${productName} assessments (Insights). ` +
        "NOT the same as invite completion percentage (see portfolio.companies.completionPct).",
    reportReadyCount: scored.length,
    companies: scored,
    topOverall: scored[0] || null,
    notReportReadySample: notReady,
  };
}

function portfolioSnapshot(allInvites) {
  const list = allInvites || [];
  const built = buildInvitesListContext(list, "", null, null, list.length);
  return {
    totalLoaded: list.length,
    kpis: built.kpis,
    companyRollup: built.companyRollup,
    companies: built.companies,
    participants: built.participants,
  };
}

function companyInviteSnapshot(rows, companyName) {
  const list = rows || [];
  const built = buildInvitesListContext(list, "", null, null, list.length);
  const rollup =
    built.companies[0] ||
    (companyName
      ? {
          name: companyName,
          company: companyName,
          invited: 0,
          completed: 0,
          completionPct: 0,
          stuck: 0,
        }
      : null);
  return {
    companyName: companyName || null,
    kpis: built.kpis,
    rollup: rollup
      ? {
          ...rollup,
          name: rollup.name || rollup.company || companyName,
        }
      : null,
    participants: list.slice(0, 100).map(serializeInviteRow).filter(Boolean),
    statusBreakdown: INVITE_STATUSES.reduce((acc, status) => {
      acc[status] = list.filter((r) => r.status === status).length;
      return acc;
    }, {}),
  };
}

/**
 * Full Ask 5x context for /engagement (Invite Participants wizard).
 */
export function buildInviteWizardContext({
  step,
  companyMode,
  companyName,
  selectedCompanyId,
  companyHasLogo,
  newCompanyDraft,
  preSurvey,
  emailTemplate,
  autoSuggestedTemplate,
  templateIsManual,
  editorHtml,
  emailExpanded,
  participants,
  pendingSendInvites,
  result,
  duplicateConfirm,
  companiesCatalog,
  companiesLoading,
  allInvites,
  invitesLoading,
  existingInvitesForCompany,
  insightsOrganizations,
  insightsLoading,
  loading,
  step1Saving,
  bulkModalOpen,
  canProceedStep1,
  recipientEmails,
  participantRoles,
  workspace,
  assessmentMode,
  offerLiteReport,
  litePathAvailable,
}) {
  const name = (companyName || "").trim();
  const liteOk =
    typeof litePathAvailable === "boolean"
      ? litePathAvailable
      : workspaceLiteInviteAvailable(workspace);
  const inviteDefaults = workspaceInviteDefaults(workspace);
  const effectiveMode = liteOk
    ? assessmentMode || inviteDefaults.assessment_mode
    : "full_only";
  const effectiveOffer = liteOk
    ? offerLiteReport || inviteDefaults.offer_lite_report
    : "N";
  const roles =
    Array.isArray(participantRoles) && participantRoles.length > 0
      ? participantRoles
      : rolesFromWorkspace(workspace);
  const isClassicRoles =
    roles.length === CLASSIC_INVITE_ROLES.length &&
    CLASSIC_INVITE_ROLES.every((r) => roles.includes(r));
  const productDisplayName =
    workspace?.product_display_name?.trim() ||
    workspace?.name?.trim() ||
    (workspace?.email_pack === PACK_GENERIC ? "Team Assessment" : "5x Team Assessment");
  const emailPack =
    workspace?.email_pack === PACK_GENERIC || workspace?.id
      ? PACK_GENERIC
      : "5x";
  const catalog = (companiesCatalog || []).map((c) => ({
    id: c.id,
    name: (c.company_name || c.name || "").trim(),
    hasLogo: !!(c.company_logo && String(c.company_logo).trim()),
  }));
  const catalogNames = catalog.map((c) => c.name).filter(Boolean);

  const validRows = participants.filter(
    (p) => p.full_name?.trim() && p.designation && p.email?.trim() && !p.emailError
  );
  const invalidRows = participants.filter(
    (p) =>
      (p.full_name?.trim() || p.email?.trim()) &&
      !(p.full_name?.trim() && p.designation && p.email?.trim() && !p.emailError)
  );
  const roleBreakdown = {};
  for (const p of participants) {
    const role = p.designation || "Unknown";
    if (!p.full_name?.trim() && !p.email?.trim()) continue;
    roleBreakdown[role] = (roleBreakdown[role] || 0) + 1;
  }

  const isFiveXWorkspace = !workspace?.id || workspace?.email_pack !== PACK_GENERIC;
  const portfolio = portfolioSnapshot(allInvites);
  const assessmentScores = buildAssessmentScoresContext(insightsOrganizations, {
    isFiveX: isFiveXWorkspace,
    productName: productDisplayName,
  });
  const selectedCompanyInvites = name
    ? companyInviteSnapshot(existingInvitesForCompany, name)
    : null;

  const scopeParts = [`Step ${step}: ${STEP_LABELS[step - 1] || "Unknown"}`];
  if (name) scopeParts.push(`company "${name}"`);
  if (step === 2) scopeParts.push(`${validRows.length} valid participant row(s) ready to send`);
  if (portfolio.totalLoaded) scopeParts.push(`${portfolio.totalLoaded} invites loaded portfolio-wide`);

  const html = String(editorHtml || "");
  const selectedCompanyRollup =
    selectedCompanyInvites?.rollup ||
    portfolio.companies.find((c) => (c.name || "").toLowerCase() === name.toLowerCase()) ||
    null;

  return {
    page: "invite",
    surface: "engagement",
    currentRoute: LENS_ROUTES.engagementCompose,
    pagePath: LENS_ROUTES.engagementCompose,
    activeLens: "compose",
    contextGuide:
      "Invite Participants wizard (/engagement). Use inviteProgram for flow rules; companyPicker for dropdown; portfolio for invite completion % (completionPct); assessmentScores for 5x overall assessment scores (overall field); selectedCompany for wizard state. overall score questions must use assessmentScores, NOT completionPct.",
    scope: {
      label: "Invite participants",
      text: scopeParts.join(" · "),
    },
    howToAddNewCompany: {
      summary:
        'On step 1 (Company Profile), choose the "Add new company" radio (not "Select existing company"), enter the company name, optionally upload a logo, then click "Next: Participants".',
      steps: [
        'Go to step 1 — Company Profile (use Back if you are on step 2).',
        'Select the radio button "Add new company" (instead of "Select existing company").',
        'Enter the company name (required).',
        'Optionally upload a company logo (PNG/JPG, max 2 MB).',
        'Click "Next: Participants" to save the company and continue to add participants.',
      ],
      currentStep: step,
      currentMode: companyMode,
      alreadyInNewCompanyMode: companyMode === "new",
    },
    inviteProgram: {
      steps: STEP_LABELS.map((label, i) => ({ step: i + 1, label })),
      liteInviteAvailable: liteOk,
      assessmentPathLabel: assessmentModeLabel(effectiveMode),
      assessmentMode: effectiveMode,
      offerLiteReport: effectiveOffer,
      assessmentPathReadOnly: true,
      participantRoles: [...roles],
      inviteStatuses: [...INVITE_STATUSES],
      emailTemplates: isClassicRoles
        ? [
            { id: EMAIL_TEMPLATE_LEADERSHIP, label: TEMPLATE_LABELS[EMAIL_TEMPLATE_LEADERSHIP] },
            { id: EMAIL_TEMPLATE_PE_BOARD, label: TEMPLATE_LABELS[EMAIL_TEMPLATE_PE_BOARD] },
          ]
        : [{ id: EMAIL_TEMPLATE_LEADERSHIP, label: "Leadership / default audience" }],
      defaultEmailSubject: inviteSubjectForPack(emailPack),
      preSurveyEnabled: preSurvey === "Y",
      peBoardRoles: isClassicRoles ? ["PE Sponsor", "Board Member"] : [],
      templateSuggestionRule: isClassicRoles
        ? "PE/Board template when more than half of roles are PE Sponsor or Board Member; otherwise Leadership template."
        : "Audience templates are resolved from workspace designation match rules; unmatched titles use the default audience.",
      productDisplayName,
    },
    companyPicker: {
      count: catalogNames.length,
      companiesLoading: !!companiesLoading,
      canSelectExisting: catalogNames.length > 0,
      canAddNewCompany: true,
      companies: catalog,
    },
    portfolio,
    assessmentScores,
    selectedCompany: {
      id: selectedCompanyId || null,
      name: name || null,
      mode: companyMode,
      hasLogo: !!companyHasLogo,
      canProceedStep1: !!canProceedStep1,
      newCompanyDraft:
        companyMode === "new"
          ? {
              company_name: (newCompanyDraft?.company_name || "").trim() || null,
              hasLogo: !!(newCompanyDraft?.company_logo && String(newCompanyDraft.company_logo).trim()),
            }
          : null,
      rollup: selectedCompanyRollup,
    },
    selectedCompanyInvites,
    wizard: {
      step,
      stepLabel: STEP_LABELS[step - 1] || "",
      companyMode,
      companyName: name || null,
      selectedCompanyId: selectedCompanyId || null,
      existingCompanyCount: catalogNames.length,
      preSurvey,
      emailTemplate: emailTemplate ?? null,
      emailTemplateLabel: emailTemplate != null ? TEMPLATE_LABELS[emailTemplate] : null,
      autoSuggestedTemplate,
      autoSuggestedTemplateLabel:
        autoSuggestedTemplate != null ? TEMPLATE_LABELS[autoSuggestedTemplate] : null,
      templateIsManual,
      participantRowCount: participants.length,
      validParticipantCount: validRows.length,
      invalidParticipantCount: invalidRows.length,
      roleBreakdown,
      pendingResendCount: pendingSendInvites?.length || 0,
      lastSendCount: result?.invites?.length || 0,
      bulkModalOpen: !!bulkModalOpen,
    },
    email: {
      defaultSubject: inviteSubjectForPack(emailPack),
      selectedTemplateId: emailTemplate ?? null,
      selectedTemplateLabel: emailTemplate != null ? TEMPLATE_LABELS[emailTemplate] : null,
      autoSuggestedTemplateId: autoSuggestedTemplate ?? null,
      templateIsManual,
      editorConfigured: html.length > 0,
      editorCharLength: html.length,
      usesMergeTags: html.includes("{{first_name}}") || html.includes("{{last_name}}"),
      sectionExpanded: !!emailExpanded,
      recipientCount: (recipientEmails || []).length,
      recipientEmails: (recipientEmails || []).slice(0, 40),
    },
    draftParticipants: participants.slice(0, 50).map((p, index) => ({
      row: index + 1,
      full_name: p.full_name?.trim() || "",
      email: p.email?.trim() || "",
      designation: p.designation || "",
      valid: !!(p.full_name?.trim() && p.designation && p.email?.trim() && !p.emailError),
      emailError: p.emailError || null,
    })),
    pendingResend: (pendingSendInvites || []).slice(0, 50).map(serializeInviteRow).filter(Boolean),
    lastSendResult: result
      ? {
          inviteCount: (result.invites || []).length,
          invites: (result.invites || []).slice(0, 50).map((inv) => ({
            invite_id: inv.invite_id,
            email: inv.email,
            first_name: inv.first_name,
            last_name: inv.last_name,
            designation: inv.designation,
            has_survey_token: !!inv.survey_token,
          })),
        }
      : null,
    duplicateEmailGate: duplicateConfirm
      ? {
          email: duplicateConfirm.email || null,
          username: duplicateConfirm.username || null,
          otherCompanies: duplicateConfirm.otherCompanies || [],
          confirmationReason: duplicateConfirm.confirmationReason || null,
        }
      : null,
    ui: {
      loading: !!loading,
      step1Saving: !!step1Saving,
      companiesLoading: !!companiesLoading,
      invitesLoading: !!invitesLoading,
      insightsLoading: !!insightsLoading,
    },
    // Top-level fields for chat fallbacks
    companyPickerCount: catalogNames.length,
    companiesCatalog: catalog,
    kpis: selectedCompanyInvites?.kpis || portfolio.kpis,
    companyRollup: portfolio.companyRollup,
    companies: assessmentScores.companies.length
      ? assessmentScores.companies
      : portfolio.companies,
    portfolioCompanies: portfolio.companies,
    organizationRows: selectedCompanyInvites
      ? selectedCompanyInvites.participants.map((p) => ({
          name: p.company_name,
          email: p.email,
          status: p.status,
          designation: p.designation,
          invited: 1,
          completed: p.status === "Completed" ? 1 : 0,
        }))
      : portfolio.participants?.slice(0, 150),
    selectedCompanyStats: selectedCompanyInvites
      ? {
          kpis: selectedCompanyInvites.kpis,
          companies: selectedCompanyInvites.rollup ? [selectedCompanyInvites.rollup] : [],
          organizationRows: selectedCompanyInvites.participants,
        }
      : undefined,
  };
}
